import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
  Circle,
  Rect,
} from '@react-pdf/renderer';
import { kkpLogoDataUrl } from './kkp-logo-data';
import { kkpCoverBgDataUrl } from './kkp-cover-bg-data';

// ─────────────────────────────────────────────────────────────────────────────
// FACTORY TEMPLATE (KKP Fine Linen)
//
// This is a standalone copy of FinalInspectionReportPDF.tsx (the QA Mate
// template). Pages 2+ are intentionally identical to the QA Mate layout; only
// these deltas differ, per the KKP "New Format 2026" spec:
//   1. Simplified KKP-branded cover page replaces the QA Mate page-1 header.
//   2. "SKU SUMMARY" -> "SKU INSPECTION SUMMARY" with relabelled columns
//      (Bundles -> Carton Qty, Insp Qty -> Offered Insp Qty, Sample -> AQL Sample).
//   3. New "DEFECT SUMMARY" table (per-SKU defect tally vs summed AQL accept nos).
//   4. Detailed SKU Report meta row gains an "AQL Sample - N" field.
//   5. KKP logo in the running header; neutral (page-number-only) last footer.
//   6. New "MEASUREMENT SAMPLING AQL" table below DEFECT SUMMARY (per-SKU
//      measurement tolerance misses vs the same AQL accept nos.; measurement
//      audits don't track major/minor severity, so misses count as Major).
//
// The QA Mate and Migros templates are NOT touched by this file.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Types (mirror FinalInspectionDetail from web) ─────────────────────────
interface ChecklistItem {
  id: string;
  title: string;
  category?: string | null;
  status?: 'pass' | 'fail' | 'na' | null;
  remarks?: string | null;
  evidence_images?: string[];
}

interface DefectItem {
  id: string;
  sku_id?: string | null;
  type?: string | null;
  title?: string | null;
  category?: string | null;
  severity?: string | null;
  level?: string | null;
  count?: number;
  location?: string | null;
  description?: string | null;
  evidence_images?: string[];
}

interface PhotoItem {
  id: string;
  sku_id?: string | null;
  tile_key?: string | null;
  label?: string | null;
  tile_label?: string | null;
  image_url: string;
  is_mandatory?: boolean;
  required?: boolean;
}

interface AuditItem {
  id: string;
  sku_id?: string | null;
  type: 'MEASUREMENT' | 'WEIGHT' | 'METAL_DETECTION';
  data?: any;
  result?: 'pass' | 'fail' | 'pending';
  notes?: string | null;
  remarks?: string | null;
}

interface SkuItem {
  id: string;
  po_sku_id?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  description?: string | null;
  sku_description?: string | null;
  ordered_qty?: number;
  quantity?: number;
  inspected_qty?: number;
  result?: 'pass' | 'fail' | 'pending';
  colour?: string | null;
  color?: string | null;
  size?: string | null;
  vendor_style?: string | null;
  vendorStyle?: string | null;
  production_completion?: number;
  productionCompletion?: number;
  metal_detection?: string | null;
  metalDetection?: string | null;
  defects?: DefectItem[];
  photoTiles?: PhotoItem[];
  photo_tiles?: PhotoItem[];
  photos?: PhotoItem[];
}

export interface FactoryFinalInspectionPDFData {
  id: string;
  po_number?: string | null;
  customer_name?: string | null;
  product_category?: string | null;
  po_ship_date?: string | null;
  po_notes?: string | null;
  inspection_type?: string | null;
  inspection_level?: string | null;
  aql_major?: string | null;
  aql_minor?: string | null;
  aql_critical?: string | null;
  lot_size?: number;
  remarks?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  overall_result: 'PASS' | 'FAIL' | 'PENDING';
  inspector_name?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  created_at: string;
  report_year?: number | null;
  report_serial?: number | null;
  facility_name?: string | null;
  facility?: { id: string; name: string } | null;
  inspector?: { id: string; name: string; profileImage?: string | null };
  checklists?: ChecklistItem[];
  defects?: DefectItem[];
  skus?: SkuItem[];
  photos?: PhotoItem[];
  audits?: AuditItem[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getSkuSampleSize = (qty: number) => {
  if (qty <= 8) return 2;
  if (qty <= 15) return 3;
  if (qty <= 25) return 5;
  if (qty <= 50) return 8;
  if (qty <= 90) return 13;
  if (qty <= 150) return 20;
  if (qty <= 280) return 32;
  if (qty <= 500) return 50;
  if (qty <= 1200) return 80;
  if (qty <= 3200) return 125;
  if (qty <= 10000) return 200;
  if (qty <= 35000) return 315;
  if (qty <= 150000) return 500;
  return 800;
};

const getAuditImages = (auditData: any): string[] => {
  if (!auditData) return [];
  const arr = Array.isArray(auditData.imageUrls) ? auditData.imageUrls
    : Array.isArray(auditData.image_urls) ? auditData.image_urls
    : Array.isArray(auditData.evidence_images) ? auditData.evidence_images
    : Array.isArray(auditData.images) ? auditData.images
    : Array.isArray(auditData.photos) ? auditData.photos
    : [];
  return (arr as string[]).filter(Boolean);
};

const getAuditDescs = (auditData: any): string[] => {
  if (!auditData) return [];
  return (Array.isArray(auditData.imageDescriptions) ? auditData.imageDescriptions
    : Array.isArray(auditData.image_descriptions) ? auditData.image_descriptions
    : Array.isArray(auditData.descriptions) ? auditData.descriptions
    : Array.isArray(auditData.captions) ? auditData.captions
    : []) as string[];
};

// ─── AQL sampling (ISO 2859-1 single sampling, normal inspection) ────────────
// Ported verbatim from the Flutter app:
//   qa-mate-inspect-pro-app-master/lib/core/utils/aql_sampling.dart
// Used only for the DEFECT SUMMARY "Total Defects allowed" row — the sum of
// per-SKU accept numbers (Ac) at each severity's AQL level.
const aqlGetCodeLetter = (lotSize: number, level: string): string => {
  const lv = (level || 'II').toUpperCase().replace('-', '');
  const pick = (breaks: [number, string][], fallback: string) => {
    for (const [max, letter] of breaks) if (lotSize <= max) return letter;
    return fallback;
  };
  if (lv === 'I') {
    return pick([[15, 'A'], [25, 'B'], [90, 'C'], [150, 'D'], [280, 'E'], [500, 'F'],
      [1200, 'G'], [3200, 'H'], [10000, 'J'], [35000, 'K'], [150000, 'L'], [500000, 'M']], 'N');
  }
  if (lv === 'III') {
    return pick([[8, 'B'], [15, 'C'], [25, 'D'], [50, 'E'], [90, 'F'], [150, 'G'], [280, 'H'],
      [500, 'J'], [1200, 'K'], [3200, 'L'], [10000, 'M'], [35000, 'N'], [150000, 'P'], [500000, 'Q']], 'R');
  }
  if (lv === 'S1') {
    return pick([[280, 'A'], [1200, 'B'], [10000, 'C'], [150000, 'D'], [500000, 'E']], 'E');
  }
  if (lv === 'S2') {
    return pick([[50, 'A'], [150, 'B'], [500, 'C'], [3200, 'D'], [35000, 'E'], [150000, 'F']], 'G');
  }
  if (lv === 'S3') {
    return pick([[15, 'A'], [50, 'B'], [150, 'C'], [280, 'D'], [1200, 'E'], [3200, 'F'],
      [35000, 'G'], [150000, 'H']], 'J');
  }
  if (lv === 'S4') {
    return pick([[50, 'A'], [150, 'B'], [500, 'C'], [1200, 'D'], [10000, 'E'], [35000, 'F'],
      [150000, 'G'], [500000, 'H']], 'H');
  }
  // General Inspection Level II (default)
  return pick([[8, 'A'], [15, 'B'], [25, 'C'], [50, 'D'], [90, 'E'], [150, 'F'], [280, 'G'],
    [500, 'H'], [1200, 'J'], [3200, 'K'], [10000, 'L'], [35000, 'M'], [150000, 'N'], [500000, 'P']], 'Q');
};

// Accept numbers (Ac) per code letter / AQL value.
const AQL_ACCEPT: Record<string, Record<string, number>> = {
  A: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 0, '4.0': 0, '6.5': 0 },
  B: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 0, '4.0': 0, '6.5': 0 },
  C: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 0, '4.0': 0, '6.5': 0 },
  D: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 0, '4.0': 0, '6.5': 1 },
  E: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 0, '4.0': 1, '6.5': 2 },
  F: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 0, '2.5': 1, '4.0': 2, '6.5': 3 },
  G: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 0, '1.5': 1, '2.5': 2, '4.0': 3, '6.5': 5 },
  H: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 0, '1.0': 1, '1.5': 2, '2.5': 3, '4.0': 5, '6.5': 7 },
  J: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 0, '0.65': 1, '1.0': 2, '1.5': 3, '2.5': 5, '4.0': 7, '6.5': 10 },
  K: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 0, '0.40': 1, '0.65': 2, '1.0': 3, '1.5': 5, '2.5': 7, '4.0': 10, '6.5': 14 },
  L: { '0.065': 0, '0.10': 0, '0.15': 0, '0.25': 1, '0.40': 2, '0.65': 3, '1.0': 5, '1.5': 7, '2.5': 10, '4.0': 14, '6.5': 21 },
  M: { '0.065': 0, '0.10': 1, '0.15': 2, '0.25': 3, '0.40': 3, '0.65': 5, '1.0': 7, '1.5': 10, '2.5': 14, '4.0': 21, '6.5': 21 },
  N: { '0.065': 0, '0.10': 1, '0.15': 2, '0.25': 3, '0.40': 4, '0.65': 7, '1.0': 10, '1.5': 14, '2.5': 21, '4.0': 21, '6.5': 21 },
  P: { '0.065': 1, '0.10': 2, '0.15': 3, '0.25': 5, '0.40': 7, '0.65': 10, '1.0': 14, '1.5': 21, '2.5': 21, '4.0': 21, '6.5': 21 },
  Q: { '0.065': 2, '0.10': 3, '0.15': 5, '0.25': 7, '0.40': 10, '0.65': 14, '1.0': 21, '1.5': 21, '2.5': 21, '4.0': 21, '6.5': 21 },
  R: { '0.065': 3, '0.10': 5, '0.15': 7, '0.25': 10, '0.40': 14, '0.65': 21, '1.0': 21, '1.5': 21, '2.5': 21, '4.0': 21, '6.5': 21 },
};

// Normalise an AQL string ("4", "1", "0.4") to a table key ("4.0", "1.0", "0.40").
const AQL_KEY_BY_FLOAT: [number, string][] = [
  [0.065, '0.065'], [0.10, '0.10'], [0.15, '0.15'], [0.25, '0.25'], [0.40, '0.40'],
  [0.65, '0.65'], [1.0, '1.0'], [1.5, '1.5'], [2.5, '2.5'], [4.0, '4.0'], [6.5, '6.5'],
];
const normalizeAqlKey = (raw?: string | null): string | null => {
  if (raw == null) return null;
  const v = String(raw).trim().toLowerCase();
  if (v === '' || v === '0' || v === 'not allowed') return null; // zero tolerance -> Ac 0
  if (AQL_ACCEPT.L[v] !== undefined) return v;                   // already an exact key
  const f = parseFloat(v);
  if (isNaN(f)) return null;
  let best: string | null = null;
  let bestDiff = Infinity;
  for (const [num, key] of AQL_KEY_BY_FLOAT) {
    const diff = Math.abs(num - f);
    if (diff < bestDiff) { bestDiff = diff; best = key; }        // nearest AQL column
  }
  return best;
};

// Accept number for a lot size at an inspection level and AQL value.
const aqlAcceptNumber = (lotSize: number, level: string, aqlValue?: string | null): number => {
  if (!lotSize || lotSize <= 0) return 0;
  const key = normalizeAqlKey(aqlValue);
  if (key == null) return 0; // zero tolerance / unknown -> nothing allowed
  const letter = aqlGetCodeLetter(lotSize, level);
  const row = AQL_ACCEPT[letter];
  if (!row) return 0;
  return row[key] ?? 0;
};

const severityOf = (d: DefectItem) => (d.level || d.severity || '').toLowerCase();

// Shared result-color logic used across every page: Pass -> green, Fail -> red, Not Applicable -> black.
const resultColorFor = (value: string): string => {
  const v = value.trim().toLowerCase();
  if (v === 'pass' || v === 'yes') return '#1a9e57';
  if (v === 'fail' || v === 'no') return '#d13b3b';
  return '#000000';
};

// Trim trailing zeros from AQL values but always show one decimal place:
// "2.50" -> "2.5", "4.00" -> "4.0" (never a bare integer).
const fmtAql = (v?: string | null): string => {
  if (v == null || String(v).trim() === '') return 'N/A';
  const n = parseFloat(String(v));
  if (isNaN(n)) return String(v);
  return Number.isInteger(n) ? `${n}.0` : String(n);
};

// Wrap long hyphen/slash-joined codes (e.g. "BBB-LFT-WHT-165X115") onto
// multiple lines with hard newlines so they never overflow a narrow table
// cell. @react-pdf/renderer honours "\n" but not zero-width spaces or hyphen
// break points, so pre-wrapping is the only reliable option.
const wrapCode = (raw?: string | null, maxLen = 13): string => {
  const str = String(raw ?? '');
  if (str.length <= maxLen) return str;
  const tokens = str.split(/([-/_ ])/);
  const lines: string[] = [];
  let cur = '';
  for (const t of tokens) {
    if (cur.length > 0 && cur.length + t.length > maxLen) { lines.push(cur); cur = ''; }
    cur += t;
  }
  if (cur) lines.push(cur);
  const out: string[] = [];
  for (const l of lines) {
    if (l.length > maxLen) for (let i = 0; i < l.length; i += maxLen) out.push(l.slice(i, i + maxLen));
    else out.push(l);
  }
  return out.join('\n');
};

// ─── KKP palette ────────────────────────────────────────────────────────────
const KKP_BLUE = '#1477bf';
const KKP_NAVY = '#0d3b66';
const KKP_BAR = '#dbe8f6'; // light-blue section-title bars (matches the cover)

// ─── Styles ─────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  // ── Cover page — full-bleed background image with an absolute overlay ──
  // Both layers get explicit A4 point dims so react-pdf keeps them on one page.
  cover: { backgroundColor: '#ffffff', padding: 0 },
  covLayer: { position: 'relative', width: 595, height: 841 },
  covBg: { position: 'absolute', top: 0, left: 0, width: 595, height: 841 },

  covBrand: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: KKP_NAVY, letterSpacing: 1.5 },
  covRule: { borderTopWidth: 1, borderTopColor: '#a9c0d2' },
  covTag: { fontSize: 6.5, color: '#6b8296', letterSpacing: 1.5 },

  covH1: { fontSize: 40, fontFamily: 'Helvetica-Bold', color: KKP_NAVY, lineHeight: 1 },
  covH1b: { fontSize: 40, fontFamily: 'Helvetica-Bold', color: KKP_BLUE, lineHeight: 1 },
  covSub: { fontSize: 10, color: '#5b6b7a', letterSpacing: 2.5 },

  covCard: { position: 'absolute', left: 38, top: 396, width: 300, backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row' },
  covCardL: { width: 172, paddingVertical: 12, paddingHorizontal: 14 },
  covCardR: { flex: 1, borderLeftWidth: 1, borderLeftColor: '#e2e8f0', alignItems: 'center', justifyContent: 'center', paddingVertical: 12 },
  covRow: { flexDirection: 'row', marginBottom: 7 },
  covLbl: { fontSize: 8, color: '#64748b', width: 74 },
  covVal: { fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: KKP_NAVY, flex: 1 },
  covPill: { position: 'absolute', left: 42, top: 219, flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: KKP_BLUE, borderRadius: 13, paddingVertical: 5, paddingHorizontal: 12 },
  covPillText: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#ffffff', letterSpacing: 1 },
  covResult: { fontSize: 19, fontFamily: 'Helvetica-Bold', marginTop: 4 },

  covPanels: { position: 'absolute', left: 38, top: 530, flexDirection: 'row', gap: 10 },
  covBuyer: { width: 128, backgroundColor: '#f4f7fa', borderRadius: 6, padding: 12 },
  covBuyerLbl: { fontSize: 6.5, color: '#7c93a8', letterSpacing: 1 },
  covBuyerVal: { fontSize: 9.5, fontFamily: 'Helvetica-Bold', color: KKP_NAVY, marginTop: 2 },
  covAql: { width: 184, backgroundColor: '#ffffff', borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0', padding: 12 },
  covAqlTitle: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: KKP_NAVY, marginBottom: 6, letterSpacing: 0.5 },
  covAqlRow: { flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 0.5, borderTopColor: '#eef2f6', paddingVertical: 3.5 },
  covAqlKey: { fontSize: 8, color: '#5b6b7a' },
  covAqlVal: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: KKP_NAVY },

  // Fixed running header (KKP logo + dept title)
  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  headerLogo: { width: 44, height: 34, objectFit: 'contain' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#000000' },
  titleLine2: { fontSize: 9, color: '#555555' },

  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },
  hrThin: { borderBottomWidth: 0.5, borderBottomColor: '#cccccc', marginVertical: 6 },

  // Two-column info block
  twoCol: { flexDirection: 'row', gap: 20, marginBottom: 10 },
  col: { flex: 1 },
  infoRow: { flexDirection: 'row', marginBottom: 4 },
  infoLabel: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', width: 100 },
  infoValue: { fontSize: 8.5, flex: 1 },
  aqlSubLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#555555', marginTop: 6, marginBottom: 4, textTransform: 'uppercase' },

  // Gray section bar (major sections)
  sectionHeader: { backgroundColor: KKP_BAR, paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: KKP_NAVY },

  // Lighter sub-bar (within SKU sections)
  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  // SKU summary table
  tableHead: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#000000', paddingBottom: 3, marginBottom: 2 },
  th: { fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1, paddingRight: 3 },
  thNarrow: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 52, textAlign: 'center' },
  thStatus: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 46, textAlign: 'right' },
  tableRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#dddddd', paddingVertical: 3 },
  td: { fontSize: 8.5, flex: 1, paddingRight: 3 },
  tdBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', flex: 1, paddingRight: 3 },
  tdNarrow: { fontSize: 8.5, width: 52, textAlign: 'center' },
  tdStatus: { fontSize: 8, fontFamily: 'Helvetica-Bold', width: 46, textAlign: 'right' },

  // Defect summary table
  dsHead: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#000000', paddingVertical: 3, paddingHorizontal: 2 },
  dsRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#dddddd', paddingVertical: 3, paddingHorizontal: 2 },
  dsTotalRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#bbbbbb', borderTopWidth: 1, borderTopColor: '#999999', paddingVertical: 3, paddingHorizontal: 2, backgroundColor: '#f7f7f7' },
  dsAllowRow: { flexDirection: 'row', paddingVertical: 3, paddingHorizontal: 2 },
  dsCellText: { fontSize: 7.5, flex: 1, paddingRight: 3 },
  dsCellHead: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', flex: 1, paddingRight: 3 },
  dsNum: { fontSize: 7.5, width: 34, textAlign: 'center' },
  dsNumHead: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', width: 34, textAlign: 'center' },
  dsRes: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', width: 42, textAlign: 'center' },

  // SKU detail block
  skuBar: { backgroundColor: KKP_BAR, paddingHorizontal: 8, paddingVertical: 6, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  skuBarText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: KKP_NAVY },
  skuBadge: { fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 2, borderWidth: 0.5, borderColor: '#555555' },
  skuMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, paddingHorizontal: 4, marginBottom: 6 },
  skuMetaItem: { flexDirection: 'row', gap: 3 },
  skuMetaLabel: { fontSize: 8, color: '#666666' },
  skuMetaValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },

  // Production completion
  progressLabel: { fontSize: 8, color: '#444444', paddingHorizontal: 4, marginBottom: 3 },
  progressTrack: { height: 5, backgroundColor: '#e2e8f0', marginHorizontal: 4, marginBottom: 8 },
  progressFill: { height: 5, backgroundColor: '#555555' },

  // Photo tiles (3-column layout at 158pt wide each)
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  photoTile: { width: 158 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 158, height: 118, objectFit: 'cover' },

  // Audit
  auditMetaRow: { flexDirection: 'row', gap: 24, paddingHorizontal: 4, marginBottom: 4 },
  auditMetaItem: { flexDirection: 'row', gap: 4 },
  auditMetaLabel: { fontSize: 8.5, color: '#666666' },
  auditMetaValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  auditNotes: { fontSize: 8, color: '#444444', fontStyle: 'italic', paddingHorizontal: 4, marginBottom: 4 },
  auditImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  auditImgTile: { width: 120 },
  auditImg: { width: 120, height: 90, objectFit: 'cover' },
  auditImgCaption: { fontSize: 7, color: '#666666', marginTop: 2 },

  // Metal detection
  metalRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 4, marginBottom: 6, marginTop: 4 },
  metalBadge: { fontSize: 9, fontFamily: 'Helvetica-Bold', paddingHorizontal: 8, paddingVertical: 3, borderWidth: 0.5, borderColor: '#666666' },
  metalNotes: { fontSize: 8, color: '#444444', flex: 1 },

  // Defects
  defectBox: { marginHorizontal: 4, marginBottom: 6, paddingBottom: 6, borderBottomWidth: 0.5, borderBottomColor: '#eeeeee' },
  defectTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
  defectMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginBottom: 3 },
  defectMetaItem: { flexDirection: 'row', gap: 3 },
  defectMetaLabel: { fontSize: 8, color: '#666666' },
  defectMetaValue: { fontSize: 8, fontFamily: 'Helvetica-Bold' },
  defectDesc: { fontSize: 8, color: '#444444', fontStyle: 'italic', marginBottom: 4 },
  defectImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  defectImg: { width: 120, height: 90, objectFit: 'cover' },

  // No-data note
  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  // Checklist
  checkCategory: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginTop: 6, marginBottom: 3, paddingHorizontal: 4 },
  checkRow: { flexDirection: 'row', justifyContent: 'space-between', borderBottomWidth: 0.5, borderBottomColor: '#eeeeee', paddingVertical: 4, paddingHorizontal: 6 },
  checkTitle: { fontSize: 8.5, flex: 1, marginRight: 8 },
  checkValue: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', width: 90, textAlign: 'right' },
  checkRemarks: { fontSize: 8, color: '#444444', fontStyle: 'italic', paddingHorizontal: 6, paddingBottom: 3 },
  checkImgRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingHorizontal: 6, paddingBottom: 6 },
  checkImg: { width: 80, height: 60, objectFit: 'cover' },

  // SKU divider
  skuDivider: { borderBottomWidth: 1, borderBottomColor: '#aaaaaa', marginVertical: 12 },

  // Remarks
  remarksText: { fontSize: 9, paddingHorizontal: 4, paddingVertical: 6, minHeight: 40 },

  // Signatures
  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },

  // Fixed footer
  pageFooter: {
    position: 'absolute',
    bottom: 14,
    left: 36,
    right: 36,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ───────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Image src={kkpLogoDataUrl} style={s.headerLogo} />
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>FINAL INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft} />
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight} />
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionHeaderText}>{title}</Text>
  </View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

// ─── Cover-page line icons (thin-stroke, monochrome) ─────────────────────────
type IconProps = { size?: number; color?: string };

const IconShield = ({ size = 15, color = '#8aa0b4' }: IconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M12 2.5 L20 5.5 V11 C20 16 16.4 19.8 12 21.5 C7.6 19.8 4 16 4 11 V5.5 Z"
      stroke={color} strokeWidth={1.6} fill="none" />
    <Path d="M8.5 12 L11 14.5 L15.5 9.5" stroke={color} strokeWidth={1.6} fill="none" />
  </Svg>
);

const IconGrid = ({ size = 13, color = '#ffffff' }: IconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    {[4, 14].map((x) => [4, 14].map((y) => (
      <Rect key={`${x}-${y}`} x={x} y={y} width={6} height={6} stroke={color} strokeWidth={1.5} fill="none" />
    )))}
  </Svg>
);

const IconPeople = ({ size = 13, color = '#ffffff' }: IconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx={9} cy={8} r={3.2} stroke={color} strokeWidth={1.5} fill="none" />
    <Circle cx={17} cy={9} r={2.6} stroke={color} strokeWidth={1.5} fill="none" />
    <Path d="M3.5 19 C3.5 14.5 14.5 14.5 14.5 19" stroke={color} strokeWidth={1.5} fill="none" />
    <Path d="M15 15 C19.5 14.2 20.5 16.5 20.5 19" stroke={color} strokeWidth={1.5} fill="none" />
  </Svg>
);

const IconClipboard = ({ size = 12, color = '#ffffff' }: IconProps) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M6 4 H18 V21 H6 Z" stroke={color} strokeWidth={1.7} fill="none" />
    <Path d="M9 4 V2.5 H15 V4" stroke={color} strokeWidth={1.7} fill="none" />
    <Path d="M8.5 12 L11 14.5 L15.5 9.5" stroke={color} strokeWidth={1.7} fill="none" />
  </Svg>
);

const CoverBadge = ({ result, color }: { result: string; color: string }) => (
  <Svg width={30} height={30} viewBox="0 0 24 24">
    <Circle cx={12} cy={12} r={11} fill={color} />
    {result === 'FAIL' ? (
      <Path d="M8 8 L16 16 M16 8 L8 16" stroke="#ffffff" strokeWidth={2.4} />
    ) : (
      <Path d="M6.5 12.5 L10.5 16.5 L17.5 8.5" stroke="#ffffff" strokeWidth={2.4} fill="none" />
    )}
  </Svg>
);

// ─── Main PDF Document ───────────────────────────────────────────────────────
const FactoryFinalInspectionReportPDF = ({ inspection }: { inspection: FactoryFinalInspectionPDFData }) => {
  const result = inspection.overall_result || 'PENDING';
  const inspectorName = inspection.inspector?.name || inspection.inspector_name || 'N/A';
  const supplierUnit = inspection.facility_name || inspection.facility?.name || 'N/A';
  const skus = inspection.skus || [];
  const globalAudits = inspection.audits || [];
  const globalDefects = inspection.defects || [];
  const globalPhotos = inspection.photos || [];
  const checklists = inspection.checklists || [];
  // KKP report number (KKPIR 26-001), stamped by the API when the report is
  // completed. Falls back to the QFI id for a report not yet numbered.
  const reportId = inspection.report_year && inspection.report_serial
    ? `KKPIR ${String(inspection.report_year % 100).padStart(2, '0')}-${String(inspection.report_serial).padStart(3, '0')}`
    : `QFI ${inspection.id.slice(0, 6).toUpperCase()}`;
  const level = inspection.inspection_level || 'II';

  const isPass = result === 'PASS';
  const isFail = result === 'FAIL';
  const resultColor = isPass ? '#1a9e57' : isFail ? '#d13b3b' : '#6b7280';

  const checklistByCategory = (() => {
    const groups = new Map<string, ChecklistItem[]>();
    checklists.forEach((c) => {
      const key = c.category || 'General';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    });
    return Array.from(groups.entries());
  })();

  // Mirrors the web's photo-association logic exactly
  const getSkuPhotos = (sku: SkuItem) => {
    const skuId = sku.po_sku_id || sku.id;
    const matched = skuId
      ? globalPhotos.filter((p) => p.sku_id === skuId)
      : globalPhotos.length === 0
        ? [...(sku.photoTiles || sku.photo_tiles || sku.photos || [])]
        : [];
    const withUrl = matched.filter((p) => p.image_url && p.image_url.length > 0);
    return {
      mandatory: withUrl.filter((p) => p.is_mandatory === true || p.required === true),
      optional: withUrl.filter((p) => !p.is_mandatory && !p.required),
    };
  };

  const getSkuDefects = (sku: SkuItem) => {
    const skuId = sku.po_sku_id || sku.id;
    return skuId
      ? globalDefects.filter((d) => d.sku_id === skuId)
      : globalDefects.length === 0 ? [...(sku.defects || [])] : [];
  };

  // ── DEFECT SUMMARY rows ──────────────────────────────────────────────────
  const defectSummaryRows = skus.map((sku) => {
    const qty = sku.quantity ?? sku.ordered_qty ?? 0;
    const offeredQty = sku.inspected_qty || qty;
    const skuDefects = getSkuDefects(sku);
    const countBy = (sev: string) =>
      skuDefects.filter((d) => severityOf(d) === sev).reduce((acc, d) => acc + (d.count || 1), 0);
    const critical = countBy('critical');
    const major = countBy('major');
    const minor = countBy('minor');
    const allowC = aqlAcceptNumber(qty, level, inspection.aql_critical);
    const allowM = aqlAcceptNumber(qty, level, inspection.aql_major);
    const allowMin = aqlAcceptNumber(qty, level, inspection.aql_minor);
    const pass = critical <= allowC && major <= allowM && minor <= allowMin;
    return {
      sku,
      qty,
      offeredQty,
      aqlSample: getSkuSampleSize(qty),
      critical, major, minor,
      allowC, allowM, allowMin,
      result: pass ? 'Pass' : 'Fail',
    };
  });

  const totals = defectSummaryRows.reduce(
    (acc, r) => ({
      critical: acc.critical + r.critical,
      major: acc.major + r.major,
      minor: acc.minor + r.minor,
      allowC: acc.allowC + r.allowC,
      allowM: acc.allowM + r.allowM,
      allowMin: acc.allowMin + r.allowMin,
    }),
    { critical: 0, major: 0, minor: 0, allowC: 0, allowM: 0, allowMin: 0 },
  );
  const totalsPass =
    totals.critical <= totals.allowC &&
    totals.major <= totals.allowM &&
    totals.minor <= totals.allowMin;

  // ── MEASUREMENT SAMPLING AQL rows ────────────────────────────────────────
  // Measurement audits only record a single sampleSize/passedQty pass-fail
  // tally (no major/minor split), so every out-of-tolerance measurement is
  // counted as Major here per standard garment-QC convention (Minor stays 0).
  // Allowed counts reuse the same per-SKU AQL accept numbers already computed
  // above for the DEFECT SUMMARY table, since it's the same AQL plan.
  const measurementRows = skus.map((sku) => {
    const skuId = sku.po_sku_id || sku.id;
    const measAudit = skuId
      ? globalAudits.find((a) => a.sku_id === skuId && a.type === 'MEASUREMENT')
      : undefined;
    const ad = measAudit?.data || {};
    const sampleSize = (ad.sampleSize ?? ad.sample_size ?? 0) as number;
    const passedQty = (ad.passedQty ?? ad.passed_qty ?? sampleSize) as number;
    const failed = sampleSize > 0 ? Math.max(0, sampleSize - passedQty) : 0;
    const dsRow = defectSummaryRows.find((r) => r.sku.id === sku.id);
    const allowM = dsRow?.allowM ?? 0;
    const allowMin = dsRow?.allowMin ?? 0;
    const toleranceRate = sampleSize > 0 ? (failed / sampleSize) * 100 : 0;
    return { sku, sampleSize, major: failed, minor: 0, allowM, allowMin, toleranceRate };
  });

  const measurementTotals = measurementRows.reduce(
    (acc, r) => ({
      sampleSize: acc.sampleSize + r.sampleSize,
      major: acc.major + r.major,
      minor: acc.minor + r.minor,
      allowM: acc.allowM + r.allowM,
      allowMin: acc.allowMin + r.allowMin,
    }),
    { sampleSize: 0, major: 0, minor: 0, allowM: 0, allowMin: 0 },
  );
  const measurementToleranceRate = measurementTotals.sampleSize > 0
    ? (measurementTotals.major / measurementTotals.sampleSize) * 100
    : 0;
  const measurementPass =
    measurementTotals.major <= measurementTotals.allowM &&
    measurementTotals.minor <= measurementTotals.allowMin;

  return (
    <Document title={`KKP Final Inspection Report - ${inspection.po_number || inspection.id}`}>

      {/* ═══ PAGE 1 — KKP cover (client background art + data overlay) ═══ */}
      <Page size="A4" style={s.cover}>
        <View style={s.covLayer} wrap={false}>
          <Image src={kkpCoverBgDataUrl} style={s.covBg} />

          {/* Brand lock-up (top-left) */}
          <Image src={kkpLogoDataUrl} style={{ position: 'absolute', left: 40, top: 40, width: 92, height: 70 }} />
          <Text style={[s.covBrand, { position: 'absolute', left: 40, top: 118 }]}>KKP FINE LINEN PVT LTD</Text>
          <View style={[s.covRule, { position: 'absolute', left: 40, top: 140, width: 232 }]} />
          <Text style={[s.covTag, { position: 'absolute', left: 40, top: 146 }]}>CREATING IMPRESSIONS - GLOBALLY</Text>

          {/* Headline block (left) */}
          <Text style={[s.covH1, { position: 'absolute', left: 40, top: 219 }]}>INSPECTION</Text>
          <Text style={[s.covH1b, { position: 'absolute', left: 40, top: 265 }]}>REPORT</Text>
          <View style={[s.covPill, { top: 318 }]}>
            <IconClipboard />
            <Text style={s.covPillText}>
              {`${(inspection.inspection_type || 'Final').trim()} Inspection`.toUpperCase()}
            </Text>
          </View>
          <Text style={[s.covSub, { position: 'absolute', left: 42, top: 362 }]}>FINISHED GOODS QUALITY INSPECTION</Text>

          {/* Report ID / dates / inspector + result badge */}
          <View style={s.covCard}>
            <View style={s.covCardL}>
              <View style={s.covRow}>
                <Text style={s.covLbl}>Report ID</Text>
                <Text style={s.covVal}>{reportId}</Text>
              </View>
              <View style={s.covRow}>
                <Text style={s.covLbl}>Inspection Date</Text>
                <Text style={s.covVal}>{formatDate(inspection.created_at)}</Text>
              </View>
              <View style={s.covRow}>
                <Text style={s.covLbl}>Ship Date</Text>
                <Text style={s.covVal}>{formatDate(inspection.po_ship_date)}</Text>
              </View>
              <View style={[s.covRow, { marginBottom: 0 }]}>
                <Text style={s.covLbl}>Inspector</Text>
                <Text style={s.covVal}>{inspectorName}</Text>
              </View>
            </View>
            <View style={s.covCardR}>
              <CoverBadge result={result} color={resultColor} />
              <Text style={[s.covResult, { color: resultColor }]}>{result}</Text>
            </View>
          </View>

          {/* Buyer panel + AQL sampling summary */}
          <View style={s.covPanels}>
            <View style={s.covBuyer}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginBottom: 9 }}>
                <IconPeople size={12} color="#8aa0b4" />
                <View>
                  <Text style={s.covBuyerLbl}>BUYER NAME</Text>
                  <Text style={s.covBuyerVal}>{inspection.customer_name || 'N/A'}</Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginBottom: 9 }}>
                <IconGrid size={12} color="#8aa0b4" />
                <View>
                  <Text style={s.covBuyerLbl}>PO</Text>
                  <Text style={s.covBuyerVal}>{inspection.po_number || 'N/A'}</Text>
                </View>
              </View>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 7 }}>
                <IconShield size={12} color="#8aa0b4" />
                <View>
                  <Text style={s.covBuyerLbl}>SUPPLIER UNIT</Text>
                  <Text style={s.covBuyerVal}>{supplierUnit}</Text>
                </View>
              </View>
            </View>

            <View style={s.covAql}>
              <Text style={s.covAqlTitle}>AQL SAMPLING SUMMARY</Text>
              <View style={[s.covAqlRow, { borderTopWidth: 0 }]}>
                <Text style={s.covAqlKey}>Inspection Level</Text>
                <Text style={s.covAqlVal}>{inspection.inspection_level || 'N/A'}</Text>
              </View>
              <View style={s.covAqlRow}>
                <Text style={s.covAqlKey}>Major AQL</Text>
                <Text style={s.covAqlVal}>{fmtAql(inspection.aql_major)}</Text>
              </View>
              <View style={s.covAqlRow}>
                <Text style={s.covAqlKey}>Minor AQL</Text>
                <Text style={s.covAqlVal}>{fmtAql(inspection.aql_minor)}</Text>
              </View>
              <View style={s.covAqlRow}>
                <Text style={s.covAqlKey}>Critical AQL</Text>
                <Text style={s.covAqlVal}>{fmtAql(inspection.aql_critical)}</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* ═══ PAGE 2 — SKU Inspection Summary + Defect Summary ═══ */}
      {skus.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />

          <SectionBar title="SKU INSPECTION SUMMARY" />
          <View style={s.tableHead}>
            <Text style={[s.th, { flex: 1.7 }]}>SKU / Style</Text>
            <Text style={[s.th, { flex: 1.5 }]}>Vendor Code</Text>
            <Text style={[s.th, { flex: 1.5 }]}>Description</Text>
            <Text style={s.thNarrow}>Order Qty</Text>
            <Text style={s.thNarrow}>Carton Qty</Text>
            <Text style={s.thNarrow}>Offered Insp Qty</Text>
            <Text style={s.thNarrow}>AQL Sample</Text>
            <Text style={s.thStatus}>Status</Text>
          </View>
          {skus.map((sku) => {
            const qty = sku.quantity ?? sku.ordered_qty ?? 0;
            const colour = sku.colour || sku.color || '';
            const size = sku.size || '';
            const variant = [colour, size ? `S:${size}` : ''].filter(Boolean).join(' | ');
            const statusStr = (sku.result || 'PENDING').toUpperCase();
            return (
              <View key={sku.id} style={s.tableRow}>
                <View style={{ flex: 1.7, paddingRight: 4, flexDirection: 'column' }}>
                  <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold' }}>{wrapCode(sku.sku_code || sku.sku_name || 'N/A')}</Text>
                  {variant ? <Text style={{ fontSize: 7, color: '#666666', marginTop: 1 }}>{variant}</Text> : null}
                </View>
                <Text style={[s.td, { flex: 1.5, fontSize: 8 }]}>{wrapCode(sku.vendorStyle || sku.vendor_style || '—')}</Text>
                <Text style={[s.td, { flex: 1.5, fontSize: 8 }]}>{wrapCode(sku.description || sku.sku_description || '—')}</Text>
                <Text style={s.tdNarrow}>{qty}</Text>
                <Text style={s.tdNarrow}>{Math.ceil(qty / 5)}</Text>
                <Text style={s.tdNarrow}>{sku.inspected_qty || qty}</Text>
                <Text style={s.tdNarrow}>{getSkuSampleSize(qty)}</Text>
                <Text style={[s.tdStatus, { color: resultColorFor(statusStr) }]}>{statusStr}</Text>
              </View>
            );
          })}

          <View style={{ marginTop: 16 }}>
            <SectionBar title="DEFECT SUMMARY" />
            <View style={s.dsHead}>
              <Text style={[s.dsCellHead, { flex: 1.4 }]}>SKU / Style</Text>
              <Text style={[s.dsCellHead, { flex: 1.4 }]}>Vendor Code</Text>
              <Text style={[s.dsCellHead, { flex: 1.4 }]}>Description</Text>
              <Text style={[s.dsCellHead, { flex: 0.7 }]}>Colour</Text>
              <Text style={[s.dsCellHead, { flex: 0.8 }]}>Size</Text>
              <Text style={[s.dsNumHead, { width: 34 }]}>Offered{'\n'}Insp Qty</Text>
              <Text style={[s.dsNumHead, { width: 30 }]}>AQL{'\n'}Sample</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>Crit{'\n'}{fmtAql(inspection.aql_critical)}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>Major{'\n'}{fmtAql(inspection.aql_major)}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>Minor{'\n'}{fmtAql(inspection.aql_minor)}</Text>
              <Text style={[s.dsRes, { width: 34 }]}>Result</Text>
            </View>
            {defectSummaryRows.map(({ sku, offeredQty, aqlSample, critical, major, minor, result: r }) => (
              <View key={sku.id} style={s.dsRow}>
                <Text style={[s.dsCellText, { flex: 1.4 }]}>{wrapCode(sku.sku_code || sku.sku_name || 'N/A')}</Text>
                <Text style={[s.dsCellText, { flex: 1.4 }]}>{wrapCode(sku.vendorStyle || sku.vendor_style || '—')}</Text>
                <Text style={[s.dsCellText, { flex: 1.4 }]}>{wrapCode(sku.description || sku.sku_description || '—')}</Text>
                <Text style={[s.dsCellText, { flex: 0.7 }]}>{wrapCode(sku.colour || sku.color || '—')}</Text>
                <Text style={[s.dsCellText, { flex: 0.8 }]}>{wrapCode(sku.size || '—')}</Text>
                <Text style={[s.dsNum, { width: 34 }]}>{offeredQty}</Text>
                <Text style={[s.dsNum, { width: 30 }]}>{aqlSample}</Text>
                <Text style={[s.dsNum, { width: 28 }]}>{critical}</Text>
                <Text style={[s.dsNum, { width: 28 }]}>{major}</Text>
                <Text style={[s.dsNum, { width: 28 }]}>{minor}</Text>
                <Text style={[s.dsRes, { width: 34, color: resultColorFor(r) }]}>{r}</Text>
              </View>
            ))}
            <View style={s.dsTotalRow}>
              <Text style={[s.dsCellHead, { flex: 1 }]}>Total Defects</Text>
              <Text style={[s.dsNum, { width: 34 }]} />
              <Text style={[s.dsNum, { width: 30 }]} />
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.critical}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.major}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.minor}</Text>
              <Text style={[s.dsRes, { width: 34, color: resultColorFor(totalsPass ? 'Pass' : 'Fail') }]}>{totalsPass ? 'Pass' : 'Fail'}</Text>
            </View>
            <View style={s.dsAllowRow}>
              <Text style={[s.dsCellHead, { flex: 1 }]}>Total Defects allowed</Text>
              <Text style={[s.dsNum, { width: 34 }]} />
              <Text style={[s.dsNum, { width: 30 }]} />
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.allowC}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.allowM}</Text>
              <Text style={[s.dsNumHead, { width: 28 }]}>{totals.allowMin}</Text>
              <Text style={[s.dsRes, { width: 34 }]} />
            </View>
          </View>

          <View style={{ marginTop: 16 }}>
            <SectionBar title="MEASUREMENT SAMPLING AQL" />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 2, marginTop: 4, marginBottom: 6 }}>
              <View style={{ flexDirection: 'row', gap: 4 }}>
                <Text style={s.auditMetaLabel}>Sample Size:</Text>
                <Text style={s.auditMetaValue}>{measurementTotals.sampleSize}</Text>
              </View>
              <Text style={[s.dsRes, { width: 'auto', fontSize: 9, color: resultColorFor(measurementPass ? 'Pass' : 'Fail') }]}>
                {measurementPass ? 'PASS' : 'FAIL'}
              </Text>
            </View>
            <View style={s.dsHead}>
              <Text style={[s.dsCellHead, { flex: 0.8 }]}>Size</Text>
              <Text style={[s.dsCellHead, { flex: 1 }]}>Colour</Text>
              <Text style={[s.dsCellHead, { flex: 1.4 }]}>Style</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>Major{'\n'}(Occ / Allowed)</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>Minor{'\n'}(Occ / Allowed)</Text>
              <Text style={[s.dsNumHead, { width: 60 }]}>Out of Tolerance{'\n'}Rate%</Text>
            </View>
            {measurementRows.map((r) => (
              <View key={r.sku.id} style={s.dsRow}>
                <Text style={[s.dsCellText, { flex: 0.8 }]}>{wrapCode(r.sku.size || '—')}</Text>
                <Text style={[s.dsCellText, { flex: 1 }]}>{wrapCode(r.sku.colour || r.sku.color || '—')}</Text>
                <Text style={[s.dsCellText, { flex: 1.4 }]}>{wrapCode(r.sku.sku_code || r.sku.sku_name || '—')}</Text>
                <Text style={[s.dsNum, { width: 56 }]}>{r.major} / {r.allowM}</Text>
                <Text style={[s.dsNum, { width: 56 }]}>{r.minor} / {r.allowMin}</Text>
                <Text style={[s.dsNum, { width: 60 }]}>{r.toleranceRate.toFixed(1)}%</Text>
              </View>
            ))}
            <View style={s.dsTotalRow}>
              <Text style={[s.dsCellHead, { flex: 3.2 }]}>Total Defects</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>{measurementTotals.major}</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>{measurementTotals.minor}</Text>
              <Text style={[s.dsNumHead, { width: 60 }]}>{measurementToleranceRate.toFixed(1)}%</Text>
            </View>
            <View style={s.dsAllowRow}>
              <Text style={[s.dsCellHead, { flex: 3.2 }]}>Total Defects Allowed</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>{measurementTotals.allowM}</Text>
              <Text style={[s.dsNumHead, { width: 56 }]}>{measurementTotals.allowMin}</Text>
              <Text style={[s.dsNumHead, { width: 60 }]} />
            </View>
          </View>

          <PageFooter />
        </Page>
      )}

      {/* ═══ CHECKLIST PAGE ═══ */}
      {checklistByCategory.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="CHECKLIST &amp; VERIFICATIONS" />
          {checklistByCategory.map(([category, items]) => (
            <View key={category}>
              <Text style={s.checkCategory}>{category}</Text>
              {items.map((item) => {
                const st = (item.status || 'na').toLowerCase();
                const label = st === 'pass' ? 'YES' : st === 'fail' ? 'NO' : 'NOT APPLICABLE';
                const hasRemarks = !!(item.remarks && item.remarks.trim().length > 0);
                const hasImages = !!(item.evidence_images && item.evidence_images.length > 0);
                return (
                  <View key={item.id}>
                    <View style={s.checkRow}>
                      <Text style={s.checkTitle}>{item.title}</Text>
                      <Text style={[s.checkValue, { color: resultColorFor(label) }]}>{label}</Text>
                    </View>
                    {hasRemarks && (
                      <Text style={s.checkRemarks}>Remarks: {item.remarks}</Text>
                    )}
                    {hasImages && (
                      <View style={s.checkImgRow}>
                        {item.evidence_images!.filter(src => src).map((src, i) => (
                          <Image key={i} src={src} style={s.checkImg} />
                        ))}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ))}
          <PageFooter />
        </Page>
      )}

      {/* ═══ DETAILED SKU REPORT — all SKU data nested under each SKU ═══ */}
      {skus.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="DETAILED SKU REPORT" />

          {skus.map((sku, skuIdx) => {
            const skuId = sku.po_sku_id || sku.id;
            const skuName = sku.sku_name || sku.sku_code || '—';
            const colour = sku.colour || sku.color || '';
            const size = sku.size || '';
            const vendorStyle = sku.vendorStyle || sku.vendor_style || '';
            const qty = sku.quantity ?? sku.ordered_qty ?? 0;
            const completion = (sku.productionCompletion ?? sku.production_completion ?? 0) as number;
            const completionPct = Math.min(Math.max(completion, 0), 100);
            const skuResult = (sku.result || '').toUpperCase();

            const skuAudits = skuId ? globalAudits.filter(a => a.sku_id === skuId) : [];
            const measAudit = skuAudits.find(a => a.type === 'MEASUREMENT');
            const weightAudit = skuAudits.find(a => a.type === 'WEIGHT');
            const metalAudit = skuAudits.find(a => a.type === 'METAL_DETECTION');

            let metalDetection = sku.metal_detection || sku.metalDetection || '';
            if (!metalDetection && metalAudit)
              metalDetection = (metalAudit.data?.result || metalAudit.result || '') as string;

            const skuDefects = skuId
              ? globalDefects.filter(d => d.sku_id === skuId)
              : globalDefects.length === 0 ? [...(sku.defects || [])] : [];

            const { mandatory: mandatoryPhotos, optional: optionalPhotos } = getSkuPhotos(sku);

            return (
              <View key={sku.id}>
                {/* SKU gray bar with name + result badge */}
                <View style={s.skuBar}>
                  <Text style={s.skuBarText}>
                    {skuName}{colour ? ` (${colour})` : ''}
                  </Text>
                  {skuResult ? <Text style={[s.skuBadge, { color: resultColorFor(skuResult) }]}>{skuResult}</Text> : null}
                </View>

                {/* SKU meta row */}
                <View style={s.skuMeta}>
                  {vendorStyle ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Vendor:</Text>
                      <Text style={s.skuMetaValue}>{vendorStyle}</Text>
                    </View>
                  ) : null}
                  {size ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Size:</Text>
                      <Text style={s.skuMetaValue}>{size}</Text>
                    </View>
                  ) : null}
                  {colour ? (
                    <View style={s.skuMetaItem}>
                      <Text style={s.skuMetaLabel}>Colour:</Text>
                      <Text style={s.skuMetaValue}>{colour}</Text>
                    </View>
                  ) : null}
                  <View style={s.skuMetaItem}>
                    <Text style={s.skuMetaLabel}>Order Qty:</Text>
                    <Text style={s.skuMetaValue}>{qty}</Text>
                  </View>
                  <View style={s.skuMetaItem}>
                    <Text style={s.skuMetaLabel}>Inspected Qty:</Text>
                    <Text style={s.skuMetaValue}>{sku.inspected_qty || qty}</Text>
                  </View>
                  <View style={s.skuMetaItem}>
                    <Text style={s.skuMetaLabel}>AQL Sample -</Text>
                    <Text style={s.skuMetaValue}>{getSkuSampleSize(qty)}</Text>
                  </View>
                </View>

                {/* Production completion (only if value > 0) */}
                {completionPct > 0 && (
                  <>
                    <Text style={s.progressLabel}>Production Completion: {completionPct.toFixed(0)}%</Text>
                    <View style={s.progressTrack}>
                      <View style={[s.progressFill, { width: `${completionPct}%` }]} />
                    </View>
                  </>
                )}

                {/* Mandatory photos */}
                {mandatoryPhotos.length > 0 && (
                  <>
                    <SubBar title="MANDATORY IMAGES" />
                    <View style={s.photoGrid}>
                      {mandatoryPhotos.map((photo, i) => (
                        <View key={photo.id || i} style={s.photoTile}>
                          <Text style={s.photoLabel}>
                            {photo.label || photo.tile_label || photo.tile_key || 'Photo'}
                          </Text>
                          <Image src={photo.image_url} style={s.photoImg} />
                        </View>
                      ))}
                    </View>
                  </>
                )}

                {/* Optional photos */}
                {optionalPhotos.length > 0 && (
                  <>
                    <SubBar title="OPTIONAL IMAGES" />
                    <View style={s.photoGrid}>
                      {optionalPhotos.map((photo, i) => (
                        <View key={photo.id || i} style={s.photoTile}>
                          <Text style={s.photoLabel}>
                            {photo.label || photo.tile_label || photo.tile_key || 'Photo'}
                          </Text>
                          <Image src={photo.image_url} style={s.photoImg} />
                        </View>
                      ))}
                    </View>
                  </>
                )}

                {/* Measurement Audit */}
                {measAudit ? (
                  <>
                    <SubBar title="MEASUREMENT AUDIT" />
                    {(() => {
                      const ad = measAudit.data || {};
                      const sampleSize = ad.sampleSize ?? ad.sample_size;
                      const passedQty = ad.passedQty ?? ad.passed_qty;
                      const auditResult = (measAudit.result || '').toUpperCase();
                      const notes = measAudit.notes || measAudit.remarks || ad.notes || ad.remarks || '';
                      const imgs = getAuditImages(ad);
                      const descs = getAuditDescs(ad);
                      return (
                        <>
                          <View style={s.auditMetaRow}>
                            {sampleSize !== undefined ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Sample Size:</Text>
                                <Text style={s.auditMetaValue}>{sampleSize}</Text>
                              </View>
                            ) : null}
                            {passedQty !== undefined && passedQty !== null ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Passed Qty:</Text>
                                <Text style={s.auditMetaValue}>
                                  {sampleSize !== undefined ? `${passedQty} / ${sampleSize}` : `${passedQty}`}
                                </Text>
                              </View>
                            ) : null}
                            <View style={s.auditMetaItem}>
                              <Text style={s.auditMetaLabel}>Status:</Text>
                              <Text style={[s.auditMetaValue, { color: resultColorFor(auditResult || 'N/A') }]}>{auditResult || 'N/A'}</Text>
                            </View>
                          </View>
                          {imgs.length > 0 && (
                            <View style={s.auditImgRow}>
                              {imgs.map((src, i) => (
                                <View key={i} style={s.auditImgTile}>
                                  <Image src={src} style={s.auditImg} />
                                  {descs[i] ? <Text style={s.auditImgCaption}>{descs[i]}</Text> : null}
                                </View>
                              ))}
                            </View>
                          )}
                          {notes ? <Text style={s.auditNotes}>{notes}</Text> : null}
                        </>
                      );
                    })()}
                  </>
                ) : null}

                {/* Weight Audit */}
                {weightAudit ? (
                  <>
                    <SubBar title="WEIGHT AUDIT" />
                    {(() => {
                      const ad = weightAudit.data || {};
                      const sampleSize = ad.sampleSize ?? ad.sample_size;
                      const passedQty = ad.passedQty ?? ad.passed_qty;
                      const auditResult = (weightAudit.result || '').toUpperCase();
                      const notes = weightAudit.notes || weightAudit.remarks || ad.notes || ad.remarks || '';
                      const imgs = getAuditImages(ad);
                      const descs = getAuditDescs(ad);
                      return (
                        <>
                          <View style={s.auditMetaRow}>
                            {sampleSize !== undefined ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Sample Size:</Text>
                                <Text style={s.auditMetaValue}>{sampleSize}</Text>
                              </View>
                            ) : null}
                            {passedQty !== undefined && passedQty !== null ? (
                              <View style={s.auditMetaItem}>
                                <Text style={s.auditMetaLabel}>Passed Qty:</Text>
                                <Text style={s.auditMetaValue}>
                                  {sampleSize !== undefined ? `${passedQty} / ${sampleSize}` : `${passedQty}`}
                                </Text>
                              </View>
                            ) : null}
                            <View style={s.auditMetaItem}>
                              <Text style={s.auditMetaLabel}>Status:</Text>
                              <Text style={[s.auditMetaValue, { color: resultColorFor(auditResult || 'N/A') }]}>{auditResult || 'N/A'}</Text>
                            </View>
                          </View>
                          {imgs.length > 0 && (
                            <View style={s.auditImgRow}>
                              {imgs.map((src, i) => (
                                <View key={i} style={s.auditImgTile}>
                                  <Image src={src} style={s.auditImg} />
                                  {descs[i] ? <Text style={s.auditImgCaption}>{descs[i]}</Text> : null}
                                </View>
                              ))}
                            </View>
                          )}
                          {notes ? <Text style={s.auditNotes}>{notes}</Text> : null}
                        </>
                      );
                    })()}
                  </>
                ) : null}

                {/* Metal Detection */}
                {(metalDetection || metalAudit) ? (
                  <>
                    <SubBar title="METAL DETECTION" />
                    <View style={s.metalRow}>
                      {metalDetection ? (
                        <Text style={[s.metalBadge, { color: resultColorFor(metalDetection) }]}>{metalDetection.toUpperCase()}</Text>
                      ) : null}
                      {metalAudit?.notes ? (
                        <Text style={s.metalNotes}>{metalAudit.notes}</Text>
                      ) : null}
                    </View>
                  </>
                ) : null}

                {/* Defect Findings */}
                {skuDefects.length > 0 ? (
                  <>
                    <SubBar title="DEFECT FINDINGS" />
                    {skuDefects.map((defect) => {
                      const lvl = (defect.level || defect.severity || '').toUpperCase();
                      const evidenceImgs = (defect.evidence_images || []).filter(src => src && src.length > 0);
                      return (
                        <View key={defect.id} style={s.defectBox}>
                          <Text style={s.defectTitle}>{defect.title || defect.type || 'Defect'}</Text>
                          <View style={s.defectMeta}>
                            {defect.category ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Category:</Text>
                                <Text style={s.defectMetaValue}>{defect.category}</Text>
                              </View>
                            ) : null}
                            {defect.type ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Type:</Text>
                                <Text style={s.defectMetaValue}>{defect.type}</Text>
                              </View>
                            ) : null}
                            {lvl ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Severity:</Text>
                                <Text style={s.defectMetaValue}>{lvl}</Text>
                              </View>
                            ) : null}
                            <View style={s.defectMetaItem}>
                              <Text style={s.defectMetaLabel}>Count:</Text>
                              <Text style={s.defectMetaValue}>{defect.count || 1}</Text>
                            </View>
                            {defect.location ? (
                              <View style={s.defectMetaItem}>
                                <Text style={s.defectMetaLabel}>Location:</Text>
                                <Text style={s.defectMetaValue}>{defect.location}</Text>
                              </View>
                            ) : null}
                          </View>
                          {defect.description ? (
                            <Text style={s.defectDesc}>"{defect.description}"</Text>
                          ) : null}
                          {evidenceImgs.length > 0 && (
                            <View style={s.defectImgRow}>
                              {evidenceImgs.map((src, i) => (
                                <Image key={i} src={src} style={s.defectImg} />
                              ))}
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </>
                ) : (
                  <Text style={s.noDataText}>The SKU meets all quality standards and has no defects.</Text>
                )}

                {skuIdx < skus.length - 1 && <View style={s.skuDivider} />}
              </View>
            );
          })}

          <PageFooter />
        </Page>
      )}

      {/* ═══ FINAL PAGE — Remarks + Signatures ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        <SectionBar title="FINAL REMARKS" />
        <Text style={s.remarksText}>{inspection.remarks || '—'}</Text>

        <View style={{ marginTop: 16 }}>
          <SectionBar title="SIGNATURES" />
          <View style={s.sigRow}>
            <View style={s.sigBox}>
              <Text style={s.sigLabel}>QA Inspector Signature</Text>
              {inspection.inspector_signature_url && inspection.inspector_signature_url.length > 0 ? (
                <Image src={inspection.inspector_signature_url} style={s.sigImg} />
              ) : (
                <View style={{ height: 44, marginBottom: 4 }} />
              )}
              <Text style={s.sigName}>{inspectorName}</Text>
              <Text style={s.sigRole}>QA Inspector</Text>
            </View>

            <View style={s.sigBox}>
              <Text style={s.sigLabel}>Factory Representative Signature</Text>
              {inspection.factory_rep_signature_url && inspection.factory_rep_signature_url.length > 0 ? (
                <Image src={inspection.factory_rep_signature_url} style={s.sigImg} />
              ) : (
                <View style={{ height: 44, marginBottom: 4 }} />
              )}
              <Text style={s.sigName}>Factory Quality Manager</Text>
              <Text style={s.sigRole}>Factory Rep</Text>
            </View>

            <View style={[s.sigBox, { flex: 0.7 }]}>
              <Text style={s.sigLabel}>Inspection Date</Text>
              <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
                <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>{formatDate(inspection.created_at)}</Text>
              </View>
              <Text style={s.sigRole}>Date of Final Audit</Text>
            </View>
          </View>
        </View>

        {/* Neutral footer on last page (page number only) */}
        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'center', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text
            style={{ fontSize: 7.5, color: '#555555', textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>

    </Document>
  );
};

export default FactoryFinalInspectionReportPDF;
